"use client";

import React, { useRef, useState, useCallback } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
  Variants,
} from "framer-motion";
import { ShopItem } from "../../shop.types";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";
import { useAudioEffects } from "../../../../../../hooks/use-audio-effects";
import clickSound from "../../../../assets/audio/menu-open.wav";
import apointsImg from "../../../vip/assets/images/apoints.png";
import ShopOfferModal from "../ShopOfferModal/ShopOfferModal";

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface OfferCardProps {
  item: ShopItem;
  className?: string;
  /** Optional callback fired after the modal opens */
  onOpen?: (item: ShopItem) => void;
  /** Optional callback fired when the user confirms purchase */
  onPurchase?: (item: ShopItem) => void;
}

// ─── Variants ──────────────────────────────────────────────────────────────────

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 50, scale: 0.85, filter: "blur(5px)" },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 260, damping: 22, mass: 0.75 },
  },
  exit: {
    opacity: 0,
    y: -30,
    scale: 0.9,
    filter: "blur(4px)",
    transition: { duration: 0.2, ease: [0.6, 0, 1, 0.4] },
  },
};

const badgeVariants: Variants = {
  hidden: { opacity: 0, scale: 0.7, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 320,
      damping: 22,
      delay: i * 0.055,
    },
  }),
};

const apBadgeVariants: Variants = {
  hidden: { opacity: 0, scale: 0.7, x: 8 },
  visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: { type: "spring", stiffness: 380, damping: 20, delay: 0.15 },
  },
};

// ─── OfferCard ─────────────────────────────────────────────────────────────────

const OfferCard = React.memo<OfferCardProps>(
  ({ item, className = "", onOpen, onPurchase }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const prefersReduced = useReducedMotion();
    const { playEffect } = useAudioEffects();

    const [hovered, setHovered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    // rotateX/rotateY removed — 3D tilt required per-frame spring recalcs on
    // every mousemove, which is expensive. The cursor shimmer (below) uses the
    // same mx/my but only updates a CSS gradient — much cheaper.
    const spring = { stiffness: 260, damping: 32, mass: 0.4 };
    const shimmerX = useSpring(useTransform(mx, [-0.5, 0.5], [15, 85]), spring);
    const shimmerY = useSpring(useTransform(my, [-0.5, 0.5], [15, 85]), spring);
    const shimmerBg = useMotionTemplate`radial-gradient(circle at ${shimmerX}% ${shimmerY}%, rgba(255,255,255,0.1) 0%, transparent 60%)`;

    const onMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (prefersReduced || !cardRef.current) return;
        const { left, top, width, height } =
          cardRef.current.getBoundingClientRect();
        mx.set((e.clientX - left) / width - 0.5);
        my.set((e.clientY - top) / height - 0.5);
      },
      [prefersReduced, mx, my],
    );

    const onMouseLeave = useCallback(() => {
      mx.set(0);
      my.set(0);
      setHovered(false);
    }, [mx, my]);

    const handleClick = useCallback(() => {
      playEffect(clickSound);
      setIsModalOpen(true);
      onOpen?.(item);
    }, [playEffect, item, onOpen]);

    const handlePurchase = useCallback(
      (purchased: ShopItem) => {
        setIsModalOpen(false);
        onPurchase?.(purchased);
      },
      [onPurchase],
    );

    const handleClose = useCallback(() => setIsModalOpen(false), []);

    const animated = !prefersReduced;

    return (
      <>
        <motion.div
          ref={cardRef}
          variants={cardVariants}
          onMouseMove={onMouseMove}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={onMouseLeave}
          whileTap={{
            scale: 0.96,
            transition: { type: "spring", stiffness: 500, damping: 30 },
          }}
          style={{
            willChange: "transform",
          }}
          className={`relative cursor-pointer rounded-md overflow-hidden ${className}`}
        >
          {/* Cursor shimmer */}
          {animated && (
            <motion.div
              className="absolute inset-0 z-20 pointer-events-none rounded-md"
              style={{ background: shimmerBg }}
              animate={{ opacity: hovered ? 1 : 0 }}
              transition={{ duration: 0.25 }}
            />
          )}

          {/* Hover border + glow */}
          <motion.div
            className="absolute inset-0 z-20 pointer-events-none rounded-md"
            animate={{
              opacity: hovered && animated ? 1 : 0,
              boxShadow:
                hovered && animated
                  ? "inset 0 0 0 1px rgba(255,255,255,0.18), 0 16px 50px rgba(0,0,0,0.45)"
                  : "inset 0 0 0 1px rgba(255,255,255,0)",
            }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />

          <BackgroundImage
            imageSrc={item.backgroundImage}
            imageClassName="!object-fill rounded-md"
            className="h-full"
          >
            <div
              onClick={handleClick}
              className="py-7 px-8 relative h-full min-h-[130px] 3440px:min-h-[170px] overflow-hidden"
            >
              {/* AP badge — top-right corner */}
              {item.apoints !== undefined && (
                <motion.div
                  variants={apBadgeVariants}
                  className="absolute top-5 right-5 z-30 flex items-center gap-2 rounded-xl px-3 py-1.5"
                  style={{
                    // backdropFilter: blur removed — always-active blur behind
                    // a small badge is disproportionately expensive in CEF.
                    // Replaced with an opaque-enough dark gradient that looks identical.
                    background:
                      "linear-gradient(135deg, rgba(30,40,55,0.88) 0%, rgba(20,28,40,0.92) 100%)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    boxShadow:
                      "0 2px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.15)",
                  }}
                  animate={
                    hovered && animated
                      ? { scale: 1.1, y: -2 }
                      : { scale: 1, y: 0 }
                  }
                  transition={{ type: "spring", stiffness: 380, damping: 20 }}
                >
                  <img
                    src={apointsImg}
                    width={26}
                    height={26}
                    draggable={false}
                  />
                  <span className="gilroy-font !font-[600] text-white text-[1.2rem] leading-none drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
                    {item.apoints}
                  </span>
                </motion.div>
              )}

              {/* Text content */}
              <div className="w-full h-full flex flex-col justify-center items-center">
                <motion.h2
                  className={`w-full text-[2rem] 3440px:text-[2.35rem] !font-bold text-white bebas-neue-font leading-[1] ${item.textClassName ?? ""}`}
                  animate={
                    hovered && animated
                      ? {
                          y: -3,
                          textShadow:
                            "0 0 14px rgba(255,255,255,0.55), 0 0 30px rgba(255,255,255,0.18)",
                        }
                      : {
                          y: 0,
                          textShadow:
                            "0 0 6px rgba(255,255,255,0.3), 0 0 11px rgba(255,255,255,0.1)",
                        }
                  }
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                >
                  {item.name}
                </motion.h2>

                {item.description && (
                  <motion.p
                    className={`w-full chakra-petch-font text-[#EEF5FB] text-[0.8rem] 3440px:text-[0.95rem] ${item.textClassName ?? ""}`}
                    animate={
                      hovered && animated
                        ? { opacity: 0.75, y: -1 }
                        : { opacity: 0.5, y: 0 }
                    }
                    transition={{ duration: 0.28, ease: "easeOut" }}
                  >
                    {item.description}
                  </motion.p>
                )}

                {item.features && (
                  <div className="flex items-center justify-start w-full gap-1 mt-1">
                    {item.features.map((feature, i) => (
                      <motion.div
                        key={i}
                        custom={i}
                        variants={badgeVariants}
                        className="bg-[#000000]/10 rounded-md py-1 px-3 flex items-center space-x-2"
                        whileHover={
                          animated
                            ? {
                                scale: 1.07,
                                backgroundColor: "rgba(255,255,255,0.08)",
                              }
                            : {}
                        }
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 20,
                        }}
                      >
                        <span className="text-white gilroy-font !font-[600] text-[0.89rem]">
                          {feature.text}
                        </span>
                        {feature.icon && (
                          <feature.icon className="text-white" />
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Decorative icon — parallax drift on hover */}
              {item.icon && (
                <motion.img
                  src={item.icon}
                  draggable={false}
                  className="absolute bottom-0 right-0 w-full max-w-[250px] z-[-1] pointer-events-none"
                  animate={
                    hovered && animated
                      ? {
                          scale: 1.06,
                          filter: "drop-shadow(0 0 18px rgba(255,255,255,0.2))",
                        }
                      : {
                          scale: 1,
                          filter: "drop-shadow(0 0 0px rgba(255,255,255,0))",
                        }
                  }
                  transition={{ type: "spring", stiffness: 260, damping: 26 }}
                />
              )}
            </div>
          </BackgroundImage>
        </motion.div>

        <ShopOfferModal
          item={item}
          isOpen={isModalOpen}
          onClose={handleClose}
          onPurchase={handlePurchase}
        />
      </>
    );
  },
);

OfferCard.displayName = "OfferCard";
export default OfferCard;
